// The Roundstead components the assistant may compose, for OpenUI. The model chooses components and writes words;
// amounts, references and states come from the record store through RecordsContext, never from the model.
import { createContext, createElement as h, useContext, type ReactNode } from 'react';
import { defineComponent, createLibrary, useTriggerAction } from '@openuidev/react-lang';
import { z } from 'zod';
import { INDOOR, OUTDOOR, type Facts } from './facts.ts';
import { unitsPhrase, type Pricing, type Quote, type QuoteLine, type UnitBasis } from './pricing.ts';
import type { Household } from './ledger.ts';
import type { Scenario } from './scenario.ts';

export interface PhotoRecord { id: string; name: string; state: 'received' | 'uploading' | 'failed' }
export interface Records {
  photos: PhotoRecord[]; facts: Facts | null; stopped?: string[]; scenario?: Scenario; started?: boolean;
  household: Household; basis: UnitBasis; pricing: Pick<Pricing, 'membership' | 'visit_by_units' | 'status'>;
  purchase: { quote: Quote; lines: QuoteLine[]; total: number | null } | null;
  commitments: { kind: string; id: string; text: string }[];
  /** Where the household got to, so a return on another device resumes there after the check. */
  progress?: { view: number; page: string | null } | null;
}
export const RecordsContext = createContext<Records | null>(null);

/** Words shown by components. Sourced strings are checked against content.json by library.test.ts. */
export const WORDS = {
  cantGetPhoto: "I can't get a photo", contactBob: 'Contact BOB', edit: 'Correct', testPrices: 'Approved test prices',
  membership: 'Club membership', perYear: 'per year · includes no visit', visit: 'Scheduled HVAC visit', perVisit: 'per visit',
  oneUnit: 'one unit', twoUnits: 'two units', threeUnits: 'three units', fourPlus: 'Four or more units', scoped: 'needs a scoped quote · no approved price',
  yourPurchase: 'Your purchase', total: 'Total', notVerified: 'Reported, not verified by a technician.', bobHandling: 'BOB is handling this',
  actionNeeded: 'Action needed from you', addPhotos: 'Add accessible photos (optional)', unresolvedCharges: '[applicable taxes or other quoted charges unresolved]',
  noVisitYet: 'Membership alone · no visit chosen', suggests: 'Photos suggest', notInventory: 'a suggestion, not a verified inventory',
} as const;

const cls = (...c: (string | false | undefined)[]) => c.filter(Boolean).join(' ');
const row = (k: ReactNode, v: ReactNode) => h('div', { className: 'hc-list__row' }, h('span', null, k), h('span', { className: 'hc-list__value' }, v));
const chip = (tone: string, text: string) => h('span', { className: `hc-chip hc-chip--${tone}` }, text);
const money = (n: number | null) => (n === null ? '—' : `$${n}`);

function Btn({ label, action, kind = 'secondary', params }: { label: string; action: string; kind?: string; params?: Record<string, unknown> }) {
  const trigger = useTriggerAction();
  return h('button', { type: 'button', className: cls('hc-btn', `hc-btn--${kind}`), onClick: () => trigger(label, undefined, { type: action, params: params ?? {} }) }, label);
}

const Reply = defineComponent({
  name: 'Reply', description: 'Root of every reply: a vertical stack of the other components, in reading order.',
  props: z.object({ children: z.array(z.any()).describe('the components of this reply') }),
  component: ({ props, renderNode }) => h('div', { className: 'hc-stack rs-reply' }, renderNode(props.children)),
});

const Message = defineComponent({
  name: 'Message', description: 'A short plain-language paragraph from the assistant. Concise; no greetings; no amounts.',
  props: z.object({ text: z.string() }),
  component: ({ props }) => h('div', { className: 'hc-bubble hc-bubble--steward' }, props.text),
});

const PhotoRequest = defineComponent({
  name: 'PhotoRequest', description: 'Invite optional photos of equipment the household can reach easily and safely. Never a requirement; nothing needs opening or climbing. Shows the "I can\'t get a photo" option.',
  props: z.object({ text: z.string().describe('one sentence saying what could help'), items: z.array(z.string()).describe('short names of helpful photos, e.g. "Outdoor unit"') }),
  component: ({ props }) => h('section', { className: 'hc-card' },
    h('p', { style: { margin: 0 } }, props.text),
    props.items.length ? h('ul', { className: 'hc-list' }, props.items.map((i, n) => h('li', { key: n, className: 'hc-list__row' }, i))) : null,
    h('div', { className: 'hc-actions' }, h(Btn, { label: WORDS.addPhotos, action: 'add_photos', kind: 'primary' }), h(Btn, { label: WORDS.cantGetPhoto, action: 'cant_get_photo' }))),
});

const PhotosReceived = defineComponent({
  name: 'PhotosReceived', description: 'Shows the photos received so far, from the record store. Takes no arguments.',
  props: z.object({}),
  component: () => {
    const r = useContext(RecordsContext); const photos = r?.photos ?? [];
    const got = photos.filter((p) => p.state === 'received').length;
    return h('section', { className: 'hc-card' },
      h('ul', { className: 'hc-list' }, photos.map((p) => h('li', { key: p.id, className: cls('hc-photo', p.state === 'failed' && 'hc-photo--failed') }, h('span', null, p.name), h('span', { className: 'hc-caption' }, p.state)))),
      h('p', { className: 'hc-caption', style: { margin: 0 } }, `${got} photos received`));
  },
});

const LABEL: Record<string, string> = { outdoor_condenser: 'Outdoor unit', heat_pump_outdoor: 'Heat pump, outdoor unit', package_unit: 'Packaged unit', mini_split_outdoor: 'Mini-split, outdoor unit',
  furnace: 'Furnace', air_handler: 'Indoor air handler', mini_split_indoor: 'Mini-split, indoor unit', boiler: 'Boiler', other_hvac: 'Other heating or cooling equipment' };

const EquipmentFound = defineComponent({
  name: 'EquipmentFound', description: 'Shows what the photos appear to show and the units the household reported, from the record store, with Correct. Photos only suggest a count; they never verify inventory. Takes no arguments.',
  props: z.object({}),
  component: () => {
    const r = useContext(RecordsContext);
    const items = (r?.facts?.equipment ?? []).filter((e) => LABEL[e.kind] && e.confidence !== 'low' && (OUTDOOR.has(e.kind) || INDOOR.has(e.kind)));
    const b = r?.basis;
    const cap = (t: string) => t[0].toUpperCase() + t.slice(1);
    const suggested = b?.photoSuggested ? (b.photoSuggested >= 4 ? 'four_plus' : (b.photoSuggested as 1 | 2 | 3)) : null;
    const heading = b?.reported ? `${cap(unitsPhrase(b.reported))} — reported by you` : suggested ? `${WORDS.suggests} ${unitsPhrase(suggested)} — ${WORDS.notInventory}` : 'No units reported yet';
    return h('section', { className: 'hc-card' },
      h('div', { className: 'hc-row', style: { justifyContent: 'space-between' } }, h('span', { className: 'hc-heading' }, heading), h(Btn, { label: WORDS.edit, action: 'edit_units', kind: 'quiet' })),
      items.length ? h('ul', { className: 'hc-list' }, items.map((e, n) => h('li', { key: n, className: 'hc-list__row' }, h('span', null, LABEL[e.kind]), h('span', { className: 'hc-list__value' }, e.system !== '?' ? `Seen as ${e.system}` : '')))) : null,
      h('p', { className: 'hc-caption', style: { margin: 0 } }, WORDS.notVerified));
  },
});

/** The approved test prices, beside each other: the annual fee and the per-visit member prices. */
const OfferPrices = defineComponent({
  name: 'OfferPrices', description: 'Shows the approved test prices from the record store: the annual club membership and the separate per-visit member prices for one, two and three units, and that four or more units need a scoped quote. Use whenever price comes up. Takes no arguments; never write amounts yourself.',
  props: z.object({}),
  component: () => {
    const r = useContext(RecordsContext); const p = r?.pricing;
    if (!p) return null;
    return h('section', { className: 'hc-card', 'aria-label': WORDS.testPrices },
      h('div', { className: 'hc-row', style: { justifyContent: 'space-between' } }, h('span', { className: 'hc-heading' }, WORDS.membership), chip('neutral', WORDS.testPrices)),
      row(`${money(p.membership.per_year)} ${WORDS.perYear}`, ''),
      h('span', { className: 'hc-heading' }, `${WORDS.visit}, ${WORDS.perVisit}`),
      row(WORDS.oneUnit, money(p.visit_by_units['1'])), row(WORDS.twoUnits, money(p.visit_by_units['2'])), row(WORDS.threeUnits, money(p.visit_by_units['3'])), row(WORDS.fourPlus, WORDS.scoped));
  },
});

/** The current purchase: exactly the lines chosen and the total, from the pricing engine. */
const PurchaseCard = defineComponent({
  name: 'PurchaseCard', description: 'Shows the household\'s current purchase from the record store: membership and any separately chosen visit, each with its amount, and the total. Use when the household asks what they would pay. Takes no arguments; never write amounts yourself.',
  props: z.object({}),
  component: () => {
    const r = useContext(RecordsContext); const p = r?.purchase;
    if (!p) return h('section', { className: 'hc-card' }, h('span', { className: 'hc-heading' }, WORDS.yourPurchase), h('p', { className: 'hc-caption', style: { margin: 0 } }, 'BOB prepares it from the units you report.'));
    return h('section', { className: 'hc-card', 'aria-label': WORDS.yourPurchase },
      h('div', { className: 'hc-row', style: { justifyContent: 'space-between' } }, h('span', { className: 'hc-heading' }, WORDS.yourPurchase), chip('neutral', WORDS.testPrices)),
      h('p', { className: 'hc-caption', style: { margin: 0 } }, `Quote ${p.quote.ref} · ${p.quote.version} · ${p.quote.terms}`),
      ...p.lines.map((l) => row(`${l.label} · ${l.note}`, money(l.amount))),
      row(WORDS.total, p.total === null ? WORDS.scoped : money(p.total)),
      h('p', { className: 'hc-caption', style: { margin: 0 } }, WORDS.unresolvedCharges));
  },
});

const NextStep = defineComponent({
  name: 'NextStep', description: 'Says who owns the next action and what it is. owner is "bob" when BOB acts next, "you" when the household must act.',
  props: z.object({ owner: z.enum(['bob', 'you']), action: z.string().describe('the next action in a few plain words, no dates or durations') }),
  component: ({ props }) => h('section', { className: 'hc-card' }, chip(props.owner === 'bob' ? 'accent' : 'warning', props.owner === 'bob' ? WORDS.bobHandling : WORDS.actionNeeded), h('p', { style: { margin: 0 } }, props.action)),
});

const ContactBob = defineComponent({
  name: 'ContactBob', description: 'A button to reach a person at BOB. Use when the household asks for a person, reports a problem with purchased care, or a safety concern.',
  props: z.object({}),
  component: () => h('div', { className: 'hc-actions' }, h(Btn, { label: WORDS.contactBob, action: 'contact_bob' })),
});

export const library = createLibrary({
  components: [Reply, Message, PhotoRequest, PhotosReceived, EquipmentFound, OfferPrices, PurchaseCard, NextStep, ContactBob],
  root: 'Reply',
});
